import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Snackbar
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    Info as InfoIcon,
    Assignment as AssignmentIcon,
    AttachMoney as AttachMoneyIcon,
    List as ListIcon,
    LayersOutlined as LayersIcon
} from '@mui/icons-material';
import { LoadingState, ErrorBoundary } from '../Common';

interface DataRow {
    [key: string]: any;
    'Valor'?: number;
    'Proyecto'?: string;
    'Proveedor'?: string;
    'Link Docuware'?: {
        href: string;
    } | string;
}

interface GroupedData {
    [proyecto: string]: {
        rows: DataRow[];
        subtotal: number;
        proveedores: {
            [proveedor: string]: {
                rows: DataRow[];
                subtotal: number;
            };
        };
    };
}

interface AutorizarCPConfirmarProps {
    fileName?: string;
    medioPago?: string;
    fechaCE?: string;
    groupedData?: GroupedData;
    onBack?: () => void;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

const parseNumber = (valueText: any): number => {
    if (typeof valueText === 'number') {
        return isNaN(valueText) ? 0 : valueText;
    }
    
    if (!valueText || valueText === '') {
        return 0;
    }
    
    let cleanValue = String(valueText);
    cleanValue = cleanValue.replace(/[$₡₱₲₩₵₽₹฿\s]/g, '');
    
    if (cleanValue.includes(',')) {
        const lastCommaIndex = cleanValue.lastIndexOf(',');
        const integerPart = cleanValue.substring(0, lastCommaIndex);
        const decimalPart = cleanValue.substring(lastCommaIndex + 1);
        const cleanIntegerPart = integerPart.replace(/\./g, '');
        cleanValue = cleanIntegerPart + '.' + decimalPart;
    } else {
        cleanValue = cleanValue.replace(/\./g, '');
    }
    
    const parsedValue = parseFloat(cleanValue);
    return isNaN(parsedValue) ? 0 : parsedValue;
};

const AutorizarCPConfirmar: React.FC<AutorizarCPConfirmarProps> = ({
    fileName = '',
    medioPago = '',
    fechaCE = '',
    groupedData = {},
    onBack
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<GroupedData>({});
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // Initialize component with localStorage data if available
    useEffect(() => {
        const initializeData = () => {
            try {
                // Check if we have data in localStorage (from preview page)
                const selectedRowsData = localStorage.getItem('selectedRowsData');
                
                if (selectedRowsData) {
                    const rows: DataRow[] = JSON.parse(selectedRowsData);
                    const processedData = processGroupedData(rows);
                    setData(processedData);
                } else if (groupedData && Object.keys(groupedData).length > 0) {
                    setData(groupedData);
                } else {
                    // No data available, redirect back
                    if (onBack) {
                        onBack();
                    } else {
                        window.location.href = '/autorizar-cp-generar-ce';
                    }
                    return;
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error initializing data:', err);
                setError('Error al cargar los datos de confirmación');
                setLoading(false);
            }
        };

        initializeData();
    }, [groupedData, onBack]);

    const processGroupedData = (rows: DataRow[]): GroupedData => {
        const grouped: GroupedData = {};
        
        rows.forEach(row => {
            const proyecto = row['Proyecto'] || 'Sin Proyecto';
            const proveedor = row['Proveedor'] || 'Sin Proveedor';
            
            if (!grouped[proyecto]) {
                grouped[proyecto] = {
                    rows: [],
                    subtotal: 0,
                    proveedores: {}
                };
            }
            
            if (!grouped[proyecto].proveedores[proveedor]) {
                grouped[proyecto].proveedores[proveedor] = {
                    rows: [],
                    subtotal: 0
                };
            }
            
            grouped[proyecto].rows.push(row);
            grouped[proyecto].proveedores[proveedor].rows.push(row);
            
            if (row['Valor']) {
                const valor = parseNumber(row['Valor']);
                grouped[proyecto].subtotal += valor;
                grouped[proyecto].proveedores[proveedor].subtotal += valor;
            }
        });
        
        return grouped;
    };

    const getColumns = (): string[] => {
        const allColumns = new Set<string>();
        Object.values(data).forEach(proyecto => {
            proyecto.rows.forEach(row => {
                Object.keys(row).forEach(key => allColumns.add(key));
            });
        });
        return Array.from(allColumns);
    };

    const handleConfirm = () => {
        setConfirmDialog(true);
    };

    const handleConfirmProcess = async () => {
        setSubmitting(true);
        setConfirmDialog(false);
        
        try {
            const selectedRowsData = localStorage.getItem('selectedRowsData');
            const storedFechaCE = localStorage.getItem('fechaCE') || fechaCE;
            
            if (!selectedRowsData || !storedFechaCE) {
                throw new Error('Datos requeridos no encontrados');
            }
            
            const formData = new FormData();
            formData.append('selected_rows', selectedRowsData);
            formData.append('fecha_ce', storedFechaCE);
            formData.append('confirm_process', 'true');
            
            const response = await fetch('/autorizar-cp-generar-ce-confirmed', {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.error) {
                throw new Error(result.error);
            }
            
            if (result.job_id) {
                setSnackbar({
                    open: true,
                    message: 'Trabajo enviado exitosamente',
                    severity: 'success'
                });
                
                // Redirect to jobs dashboard
                setTimeout(() => {
                    window.location.href = `/jobs?highlight=${result.job_id}`;
                }, 2000);
            } else {
                throw new Error('No se recibió un ID de trabajo válido');
            }
        } catch (err) {
            console.error('Error processing confirmation:', err);
            setSnackbar({
                open: true,
                message: `Error: ${err instanceof Error ? err.message : 'Error desconocido'}`,
                severity: 'error'
            });
        }
        
        setSubmitting(false);
    };

    const renderTable = (rows: DataRow[], columns: string[]) => (
        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell key={col} sx={{ fontWeight: 'bold' }}>
                                {col}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.sort((a, b) => {
                        const cuentaA = String(a['Cuenta'] || '');
                        const cuentaB = String(b['Cuenta'] || '');
                        return cuentaA.localeCompare(cuentaB);
                    }).map((row, index) => (
                        <TableRow key={index}>
                            {columns.map(col => (
                                <TableCell key={col}>
                                    {col === 'Link Docuware' && typeof row[col] === 'object' && row[col]?.href ? (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            href={row[col].href}
                                            target="_blank"
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            Ver PDF
                                        </Button>
                                    ) : col === 'Valor' ? (
                                        formatCurrency(parseNumber(row[col]))
                                    ) : (
                                        row[col] || ''
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) {
        return <LoadingState type="spinner" message="Cargando datos de confirmación..." />;
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    const storedFileName = localStorage.getItem('filename') || fileName;
    const storedMedioPago = localStorage.getItem('mediopago') || medioPago;
    const storedFechaCE = localStorage.getItem('fechaCE') || fechaCE;
    const formattedFecha = storedFechaCE ? new Date(storedFechaCE).toLocaleDateString('es-CO') : '';

    const totalRows = Object.values(data).reduce((sum, proyecto) => sum + proyecto.rows.length, 0);
    const calculatedTotal = Object.values(data).reduce((sum, proyecto) => sum + proyecto.subtotal, 0);

    const columns = getColumns();

    return (
        <ErrorBoundary>
            <Box sx={{ p: 3 }}>
                <Card>
                    <CardHeader
                        title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" />
                                <Typography variant="h5">
                                    Confirmación de datos
                                </Typography>
                            </Box>
                        }
                        sx={{ bgcolor: 'success.main', color: 'white' }}
                    />
                    <CardContent>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <InfoIcon sx={{ mr: 1 }} />
                            Verifique los datos agrupados antes de confirmar el procesamiento.
                        </Alert>

                        {/* File Info */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Archivo: <span style={{ color: '#203a5d' }}>{storedFileName}</span>
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                                Medio de pago: <span style={{ color: '#203a5d' }}>{storedMedioPago}</span>
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                                Fecha del Comprobante de Egreso: <span style={{ color: '#42c4dd' }}>{formattedFecha}</span>
                            </Typography>
                        </Box>

                        {/* Statistics */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'grey.100', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ListIcon />
                                            Total de filas
                                        </Typography>
                                        <Typography variant="h3">{totalRows}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'grey.100', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoneyIcon />
                                            Valor total
                                        </Typography>
                                        <Typography variant="h3">{formatCurrency(calculatedTotal)}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Grouped Data */}
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <LayersIcon />
                            Datos agrupados:
                        </Typography>

                        {Object.keys(data).sort().map((proyecto, proyectoIndex) => {
                            const proyectoData = data[proyecto];
                            
                            return (
                                <Accordion key={proyectoIndex} defaultExpanded sx={{ mb: 2 }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ 
                                            bgcolor: '#e3f2fd',
                                            '&:hover': { bgcolor: '#99d2fa' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                                            <Typography variant="subtitle1">
                                                <strong>Proyecto:</strong> {proyecto}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Chip label={`${proyectoData.rows.length} registros`} color="primary" size="small" />
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {formatCurrency(proyectoData.subtotal)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {Object.keys(proyectoData.proveedores).sort().map((proveedor, proveedorIndex) => {
                                            const proveedorData = proyectoData.proveedores[proveedor];
                                            
                                            return (
                                                <Accordion key={proveedorIndex} defaultExpanded={false} sx={{ mb: 1 }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{ 
                                                            bgcolor: '#cff6fe',
                                                            '&:hover': { bgcolor: '#85dff1' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                                                            <Typography variant="subtitle2">
                                                                <strong>Proveedor:</strong> {proveedor}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                                <Chip label={`${proveedorData.rows.length} registros`} color="info" size="small" />
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    {formatCurrency(proveedorData.subtotal)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        {renderTable(proveedorData.rows, columns)}
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => {
                                    localStorage.setItem('returningFromConfirmation', 'true');
                                    window.location.href = '/return-to-preview';
                                }}
                            >
                                Volver
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={handleConfirm}
                                disabled={submitting}
                            >
                                {submitting ? 'Enviando...' : 'Confirmar y procesar datos'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Confirmation Dialog */}
                <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
                    <DialogTitle>
                        <AssignmentIcon sx={{ mr: 1 }} />
                        Confirmar procesamiento
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            ¿Está seguro de que desea procesar {totalRows} registros por un valor total de {formatCurrency(calculatedTotal)}?
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Esta acción enviará los datos para procesamiento y no se puede deshacer.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleConfirmProcess} variant="contained" color="success">
                            Confirmar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ErrorBoundary>
    );
};

export default AutorizarCPConfirmar;